import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from './SnackbarContext';
import { Box, IconButton } from '@mui/material';
import { Close, CloudUpload, Image as ImageIcon } from '@mui/icons-material';
import config from '../config';
import axios from 'axios';

const useStyles = makeStyles(() => ({
    rootContainer: {
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'white',
        overflow: 'hidden',
        borderRadius: 30,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Nunito, Poppins, Open Sans, Inter, sans-serif',
        padding: '20px',
        boxSizing: 'border-box',
    },
    header: {
        width: 325,
        height: 85,
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
        alignSelf: 'center',
    },
    headerTitle: {
        color: '#C084FC',
        fontSize: 20,
        fontFamily: 'Poppins',
        fontWeight: '700',
        wordWrap: 'break-word',
    },
    formContainer: {
        flex: 1,
        overflow: 'auto',
        paddingBottom: 100,
    },
    profileSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: 30,
    },
    profileImage: {
        width: 99,
        height: 99,
        borderRadius: '50%',
        border: '2px solid white',
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        marginBottom: 10,
    },
    cameraIcon: {
        width: 31,
        height: 31,
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        borderRadius: '50%',
        position: 'absolute',
        bottom: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cameraIconInner: {
        width: 19.30,
        height: 19.30,
        background: 'white',
        mask: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z\'/%3E%3Ccircle cx=\'12\' cy=\'13\' r=\'4\'/%3E%3C/svg%3E") no-repeat center',
        maskSize: 'contain',
    },
    inputGroup: {
        display: 'flex',
        gap: 10,
        marginBottom: 20,
    },
    inputField: {
        flex: 1,
        height: 51,
        borderRadius: 10,
        border: '1.02px solid #0D0D0D',
        background: '#E9E9E9',
        padding: '0 15px',
        fontSize: 18,
        fontFamily: 'Nunito',
        fontWeight: '400',
        outline: 'none',
        '&:focus': {
            border: '2px solid #C084FC',
            background: 'white',
        },
    },
    fullWidthInput: {
        width: '100%',
        height: 51,
        borderRadius: 10,
        border: '1.04px solid #262626',
        background: 'white',
        padding: '0 15px',
        fontSize: 18,
        fontFamily: 'Nunito',
        fontWeight: '500',
        outline: 'none',
        marginBottom: 20,
        '&:focus': {
            border: '2px solid #C084FC',
        },
    },
    label: {
        color: '#A1A1A1',
        fontSize: 18,
        fontFamily: 'Nunito',
        fontWeight: '500',
        marginBottom: 8,
        display: 'block',
    },
    smallLabel: {
        color: '#A1A1A1',
        fontSize: 16,
        fontFamily: 'Nunito',
        fontWeight: '400',
        marginBottom: 8,
        display: 'block',
    },
    dropdown: {
        width: '100%',
        height: 51,
        borderRadius: 10,
        border: '1.02px solid #0D0D0D',
        background: '#E9E9E9',
        padding: '0 15px',
        fontSize: 18,
        fontFamily: 'Nunito',
        fontWeight: '400',
        outline: 'none',
        '&:focus': {
            border: '2px solid #C084FC',
            background: 'white',
        },
    },
    uploadSection: {
        marginBottom: 20,
    },
    uploadContainer: {
        padding: 16,
        background: '#F7F7F7',
        borderRadius: 16,
        border: '1px solid #E5E5E5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        '&:hover': {
            background: '#F0F0F0',
        },
    },
    uploadIcon: {
        width: 32,
        height: 32,
        background: '#C084FC',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 8,
    },
    uploadText: {
        color: '#0EA5E9',
        fontSize: 12,
        fontFamily: 'Inter',
        fontWeight: '600',
        lineHeight: '18px',
        marginBottom: 4,
    },
    uploadSubtext: {
        color: '#A3A3A3',
        fontSize: 11,
        fontFamily: 'Inter',
        fontWeight: '400',
        lineHeight: '16px',
    },
    submitButton: {
        width: '100%',
        height: 51,
        borderRadius: 10.40,
        border: 'none',
        color: 'white',
        fontSize: 18,
        fontFamily: 'Nunito',
        fontWeight: '700',
        position: 'fixed',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: 'calc(100% - 40px)',
    },
    errorText: {
        color: '#f44336',
        fontSize: 14,
        textAlign: 'center',
        marginTop: 8,
        fontFamily: 'Nunito',
    },
}));

const PersonalDetails = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useSnackbar();
    
    const [formData, setFormData] = useState({
        salutation: 'Ms.',
        firstName: '',
        middleName: '',
        lastName: '',
        phone: '',
        dateOfBirth: '',
        gender: '',
        address: '',
        aadhaarNumber: '',
        drivingLicenseNumber: '',
        expiryDate: '',
        aadhaarFile: null,
        drivingLicenseFile: null,
    });
    const [filePreviews, setFilePreviews] = useState({
        aadhaarFile: null,
        drivingLicenseFile: null,
    });
    // S3 URLs of documents the employee uploaded on a prior submission.
    // Rendered as the preview when no new file has been picked in this
    // session, AND treated as satisfying the "license file required" gate
    // so a resubmitter doesn't have to re-upload a still-valid image.
    const [existingDocs, setExistingDocs] = useState({
        aadhaar: null,
        license: null,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [verificationIssue, setVerificationIssue] = useState('');

    const { userData, role } = location.state || {};

    // Initialize form data with userData + a fresh GET /users/:id so resubmit
    // flows pre-fill ALL previously-submitted values (Aadhaar, license number,
    // license validity, DOB, gender) — not just name and phone. The login
    // response carries a narrow user payload (user_id, name, phone, email,
    // first_name); the GET fills in the rest.
    useEffect(() => {
        if (!userData) return;
        const nameParts = (userData.name || '').split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        // Seed from userData immediately so the form isn't blank during fetch.
        setFormData(prev => ({
            ...prev,
            firstName: userData.first_name || firstName,
            lastName: userData.last_name || lastName,
            phone: userData.phone || '',
        }));
        setVerificationIssue(userData.verification_issue || '');

        if (!userData.user_id) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${config.API_BASE_URL}/users/${userData.user_id}`);
                if (cancelled || !r.data) return;
                const u = r.data;
                setFormData(prev => ({
                    ...prev,
                    salutation: u.salutation || prev.salutation,
                    firstName: u.first_name || prev.firstName,
                    lastName: u.last_name || prev.lastName,
                    phone: u.phone || prev.phone,
                    dateOfBirth: u.dob || prev.dateOfBirth,
                    gender: u.gender || prev.gender,
                    aadhaarNumber: u.verification_aadhar || u.aadhaar_number || prev.aadhaarNumber,
                    drivingLicenseNumber: u.license_number || prev.drivingLicenseNumber,
                    expiryDate: u.license_validity_date || u.license_expiry_date || prev.expiryDate,
                }));
                setExistingDocs({
                    aadhaar: u.verification_aadhar_image || u.aadhar_document || null,
                    license: u.verification_license_image || u.license_document || null,
                });
                if (u.verification_issue) setVerificationIssue(u.verification_issue);
            } catch (err) {
                console.warn('[PersonalDetails] /users/<id> fetch failed (using login payload only):', err?.message);
            }
        })();
        return () => { cancelled = true; };
    }, [userData]);

    // Cleanup file previews on component unmount
    useEffect(() => {
        return () => {
            Object.values(filePreviews).forEach(preview => {
                if (preview) {
                    URL.revokeObjectURL(preview);
                }
            });
        };
    }, [filePreviews]);

    const handleInputChange = (field, value) => {
        console.log(`[PersonalDetails] Field changed: ${field} = "${value}"`);
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Special logging for expiry date changes
        if (field === 'expiryDate') {
            console.log(`[PersonalDetails] Expiry date changed to: "${value}"`);
            const dateValid = value && value.trim() ? isValidDate(value) : true;
            console.log(`[PersonalDetails] Date validation result: ${dateValid}`);
        }
    };

    // Form validation
    const isValidPhone = (phone) => {
        // Remove any non-digit characters and check if it's 10 digits (Indian phone number)
        const digits = phone.replace(/\D/g, '');
        return digits.length === 10;
    };
    const isValidAadhaar = (aadhaar) => {
        if (!aadhaar || !aadhaar.trim()) return true; // Empty is valid (optional field)
        // Remove any non-digit characters and check if it's 12 digits
        const digits = aadhaar.replace(/\D/g, '');
        return digits.length === 12;
    };
    const isValidLicense = (license) => {
        // License format validation removed - any format is now accepted
        // Empty is also valid (optional field)
        return true;
    };
    const isValidDate = (date) => {
        if (!date) return true; // Optional field
        
        try {
            // Check if the date string matches YYYY-MM-DD format
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(date)) {
                console.log(`[isValidDate] Invalid date format: ${date}`);
                return false;
            }
            
            // Parse the date components
            const [year, month, day] = date.split('-').map(Number);
            
            // Check for reasonable year range (1900 to 2100)
            if (year < 1900 || year > 2100) {
                console.log(`[isValidDate] Year out of range: ${year}`);
                return false;
            }
            
            // Check for valid month (1-12)
            if (month < 1 || month > 12) {
                console.log(`[isValidDate] Invalid month: ${month}`);
                return false;
            }
            
            // Check for valid day (1-31, but also check month-specific limits)
            if (day < 1 || day > 31) {
                console.log(`[isValidDate] Invalid day: ${day}`);
                return false;
            }
            
            // Check for month-specific day limits
            const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
            
            // Handle leap year for February
            if (month === 2) {
                const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
                const maxDaysInFeb = isLeapYear ? 29 : 28;
                if (day > maxDaysInFeb) {
                    console.log(`[isValidDate] Invalid day for February ${year}: ${day} (max: ${maxDaysInFeb})`);
                    return false;
                }
            } else if (day > daysInMonth[month - 1]) {
                console.log(`[isValidDate] Invalid day for month ${month}: ${day} (max: ${daysInMonth[month - 1]})`);
                return false;
            }
            
            // Create date object and verify it's valid
            const inputDate = new Date(year, month - 1, day);
            const today = new Date();
            
            // Check if the created date matches the input (catches invalid dates like Feb 30)
            if (inputDate.getFullYear() !== year || 
                inputDate.getMonth() !== month - 1 || 
                inputDate.getDate() !== day) {
                console.log(`[isValidDate] Date object doesn't match input: ${date}`);
                return false;
            }
            
            // Set time to start of day for both dates to avoid timezone issues
            today.setHours(0, 0, 0, 0);
            inputDate.setHours(0, 0, 0, 0);
            
            console.log(`[isValidDate] Input date: ${date} -> ${inputDate.toISOString()}`);
            console.log(`[isValidDate] Today: ${today.toISOString()}`);
            console.log(`[isValidDate] Is future date: ${inputDate > today}`);
            
            // For license expiry date, it should be in the future (not expired)
            return inputDate > today;
        } catch (error) {
            console.error('[isValidDate] Error parsing date:', error);
            return false;
        }
    };

    const isFormValid = () => {
        // Phone stays required. First/Last name dropped from the required set
        // because admin-add populates only User.name (first/last stay NULL by
        // design) — the mount effect pre-fills firstName/lastName from
        // userData.name, so blanks here only happen if admin didn't type one
        // either. License Number replaces name as the verification gate.
        const phoneValid = formData.phone.trim();
        const phoneFormatValid = phoneValid ? isValidPhone(formData.phone) : false;

        // Aadhaar number remains optional; only validate format if provided.
        const aadhaarValid = !formData.aadhaarNumber || !formData.aadhaarNumber.trim() || isValidAadhaar(formData.aadhaarNumber);
        // License number is now required AND must pass format check.
        const licenseNumberPresent = formData.drivingLicenseNumber && formData.drivingLicenseNumber.trim();
        const licenseValid = !!licenseNumberPresent && isValidLicense(formData.drivingLicenseNumber);
        const dateValid = formData.expiryDate && formData.expiryDate.trim() && isValidDate(formData.expiryDate);

        // File uploads - Aadhaar optional, License file still required.
        // A new file pick OR a previously-uploaded image satisfies the
        // license-file requirement; otherwise a resubmitter would be forced
        // to re-upload a still-valid image just to dismiss the banner.
        const aadhaarFileValid = true;
        const licenseFileValid = formData.drivingLicenseFile !== null || !!existingDocs.license;

        const firstNameValid = formData.firstName.trim();
        const lastNameValid = formData.lastName.trim();
        const overallValid = phoneValid && phoneFormatValid &&
                           aadhaarValid && licenseValid && dateValid && aadhaarFileValid && licenseFileValid;
        
        console.log('=== FORM VALIDATION DEBUG ===');
        console.log('Form Data:', formData);
        console.log('Validation Results:', {
            firstName: formData.firstName,
            firstNameValid,
            lastName: formData.lastName,
            lastNameValid,
            phone: formData.phone,
            phoneValid,
            phoneFormatValid,
            aadhaarNumber: formData.aadhaarNumber,
            aadhaarValid,
            drivingLicenseNumber: formData.drivingLicenseNumber,
            licenseValid,
            expiryDate: formData.expiryDate,
            dateValid,
            aadhaarFile: formData.aadhaarFile,
            aadhaarFileValid,
            drivingLicenseFile: formData.drivingLicenseFile,
            licenseFileValid,
            overallValid
        });
        console.log('Button should be:', overallValid ? 'ENABLED (DARK)' : 'DISABLED (LIGHT)');
        console.log('===============================');

        return (
            firstNameValid &&
            phoneValid &&
            phoneFormatValid &&
            aadhaarValid &&
            licenseValid &&
            dateValid &&
            aadhaarFileValid &&
            licenseFileValid
        );
    };

    const handleFileUpload = (field, event) => {
        const file = event.target.files[0];
        if (file) {
            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            
            setFormData(prev => ({
                ...prev,
                [field]: file
            }));
            
            setFilePreviews(prev => ({
                ...prev,
                [field]: previewUrl
            }));
        }
    };

    const removeFile = (field) => {
        // Revoke the preview URL
        if (filePreviews[field]) {
            URL.revokeObjectURL(filePreviews[field]);
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: null
        }));
        
        setFilePreviews(prev => ({
            ...prev,
            [field]: null
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Basic validation
        if (!formData.firstName || !formData.phone) {
            setError('Please fill in all required fields');
            return;
        }

        setIsLoading(true);
        try {
            // Create FormData for file uploads
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', userData.user_id);
            formDataToSend.append('salutation', formData.salutation || '');
            formDataToSend.append('firstName', formData.firstName || '');
            formDataToSend.append('lastName', formData.lastName || '');
            formDataToSend.append('gender', formData.gender || '');
            formDataToSend.append('dateOfBirth', formData.dateOfBirth || '');
            formDataToSend.append('address', formData.address || '');
            formDataToSend.append('aadhaarNumber', formData.aadhaarNumber || '');
            formDataToSend.append('drivingLicenseNumber', formData.drivingLicenseNumber || '');
            formDataToSend.append('expiryDate', formData.expiryDate || '');
            
            // Add file uploads if they exist
            if (formData.aadhaarFile) {
                formDataToSend.append('aadhaarFile', formData.aadhaarFile);
            }
            if (formData.drivingLicenseFile) {
                formDataToSend.append('licenseFile', formData.drivingLicenseFile);
            }

            // Call the personal details API with FormData
            const response = await axios.post(`${config.API_BASE_URL}/update-personal-details`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.message) {
                showSnackbar('Personal details submitted successfully', 'success');
                
                // Navigate to verification pending screen
                setTimeout(() => {
                    navigate('/verification-pending', {
                        state: {
                            userData,
                            role
                        }
                    });
                }, 1000);
            }
        } catch (error) {
            setError('Failed to submit personal details. Please try again.');
            showSnackbar('Failed to submit personal details', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const isResubmit = !!verificationIssue;
    return (
        <div className={classes.rootContainer}>
            <div className={classes.header}>
                <div className={classes.headerTitle}>
                    {isResubmit ? 'Resubmit your details' : 'Personal Details'}
                </div>
            </div>

            <form className={classes.formContainer} onSubmit={handleSubmit}>
                {/* Rejection banner — only shown when the manager has sent the
                    employee back via Report Issue + Resubmit. Wording mirrors
                    the EmployeeDetailsForm "Report Issue" textarea on the
                    admin side so the employee sees the exact feedback. */}
                {isResubmit && (
                    <div style={{
                        background: '#FEF2F2',
                        border: '1px solid #FCA5A5',
                        borderRadius: 12,
                        padding: '12px 14px',
                        marginBottom: 16,
                        color: '#991B1B',
                        fontFamily: 'Nunito',
                    }}>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                            ⚠ Your submission was sent back
                        </div>
                        <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                            <strong>Reason:</strong> {verificationIssue}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 6, color: '#7F1D1D' }}>
                            Please review the form below and resubmit.
                        </div>
                    </div>
                )}

                {/* Profile Image Section */}
                <div className={classes.profileSection}>
                    <div className={classes.profileImage}>
                        <div className={classes.cameraIcon}>
                            <div className={classes.cameraIconInner} />
                        </div>
                    </div>
                </div>

                {/* Salutation and Last Name */}
                <div className={classes.inputGroup}>
                    <div style={{ width: 71 }}>
                        <select 
                            className={classes.dropdown}
                            value={formData.salutation}
                            onChange={(e) => handleInputChange('salutation', e.target.value)}
                        >
                            <option value="Ms.">Ms.</option>
                            <option value="Mr.">Mr.</option>
                            <option value="Mrs.">Mrs.</option>
                            <option value="Dr.">Dr.</option>
                        </select>
                    </div>
                    <div style={{ flex: 1 }}>
                        <input
                            className={classes.inputField}
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            placeholder="Enter last name"
                            style={{
                                borderColor: formData.lastName && !formData.lastName.trim() ? '#FF4444' : '#0D0D0D'
                            }}
                        />
                        {formData.lastName && !formData.lastName.trim() && (
                            <div style={{ color: '#FF4444', fontSize: '12px', marginTop: '4px', fontFamily: 'Nunito' }}>
                                Last name cannot be empty
                            </div>
                        )}
                    </div>
                </div>

                {/* First Name and Middle Name */}
                <div className={classes.inputGroup}>
                    <div style={{ flex: 1 }}>
                        <input
                            className={classes.inputField}
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            placeholder="Enter first name"
                            style={{
                                borderColor: formData.firstName && !formData.firstName.trim() ? '#FF4444' : '#0D0D0D'
                            }}
                        />
                        {formData.firstName && !formData.firstName.trim() && (
                            <div style={{ color: '#FF4444', fontSize: '12px', marginTop: '4px', fontFamily: 'Nunito' }}>
                                First name cannot be empty
                            </div>
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        <input
                            className={classes.inputField}
                            type="text"
                            value={formData.middleName}
                            onChange={(e) => handleInputChange('middleName', e.target.value)}
                            placeholder="Enter middle name"
                        />
                    </div>
                </div>

                {/* Phone Number */}
                <div className={classes.inputGroup}>
                    <div style={{ width: 71 }}>
                        <div className={classes.inputField} style={{ display: 'flex', alignItems: 'center' }}>
                            +91
                        </div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <input
                            className={classes.inputField}
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            placeholder="Enter phone number"
                            style={{
                                borderColor: formData.phone && !isValidPhone(formData.phone) ? '#FF4444' : '#0D0D0D'
                            }}
                        />
                        {formData.phone && !isValidPhone(formData.phone) && (
                            <div style={{ color: '#FF4444', fontSize: '12px', marginTop: '4px', fontFamily: 'Nunito' }}>
                                Please enter a valid 10-digit phone number
                            </div>
                        )}
                    </div>
                </div>

                {/* Date of Birth */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <label className={classes.label}>Date of Birth</label>
                        <span style={{ color: '#FF4444', fontSize: '16px', fontWeight: '600' }}>*</span>
                    </div>
                    <input
                        className={classes.fullWidthInput}
                        type="date"
                        min="1900-01-01"
                        max={new Date().toISOString().split('T')[0]}
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    />
                </div>

                {/* Gender */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <label className={classes.label}>Gender</label>
                        <span style={{ color: '#FF4444', fontSize: '16px', fontWeight: '600' }}>*</span>
                    </div>
                    <select 
                        className={classes.fullWidthInput}
                        value={formData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                    >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                    </select>
                </div>

                {/* Address */}
                <div>
                    <input
                        className={classes.fullWidthInput}
                        type="text"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="Enter address"
                    />
                </div>

                {/* Aadhaar Number */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <label className={classes.label}>Aadhaar Number</label>
                    </div>
                    <input
                        className={classes.fullWidthInput}
                        type="text"
                        value={formData.aadhaarNumber}
                        onChange={(e) => handleInputChange('aadhaarNumber', e.target.value)}
                        placeholder="Enter Aadhaar number (12 digits)"
                        style={{
                            borderColor: formData.aadhaarNumber && !isValidAadhaar(formData.aadhaarNumber) ? '#FF4444' : '#0D0D0D'
                        }}
                    />
                    {formData.aadhaarNumber && !isValidAadhaar(formData.aadhaarNumber) && (
                        <div style={{ color: '#FF4444', fontSize: '12px', marginTop: '4px', fontFamily: 'Nunito' }}>
                            Please enter a valid 12-digit Aadhaar number
                        </div>
                    )}
                </div>

                {/* Aadhaar Upload */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <label className={classes.label}>Aadhaar Card Upload</label>
                    </div>
                </div>
                <div className={classes.uploadSection}>
                    {formData.aadhaarFile ? (
                        <Box sx={{
                            padding: '16px',
                            background: '#E8F5E8',
                            borderRadius: '16px',
                            border: '2px solid #4CAF50',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative'
                        }}>
                            <ImageIcon sx={{ fontSize: '32px', color: '#4CAF50', marginBottom: '8px' }} />
                            <div style={{ color: '#2E7D32', fontSize: '12px', fontFamily: 'Inter', fontWeight: '600' }}>
                                Aadhaar Card Uploaded
                            </div>
                            <div style={{ color: '#666', fontSize: '11px', fontFamily: 'Inter' }}>
                                {formData.aadhaarFile.name}
                            </div>
                            <img 
                                src={filePreviews.aadhaarFile} 
                                alt="Aadhaar preview" 
                                style={{ 
                                    width: '100%', 
                                    maxWidth: '200px', 
                                    height: 'auto', 
                                    borderRadius: '8px',
                                    marginTop: '8px'
                                }} 
                            />
                            <IconButton
                                onClick={() => removeFile('aadhaarFile')}
                                sx={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    '&:hover': { background: 'rgba(255, 255, 255, 1)' }
                                }}
                            >
                                <Close sx={{ fontSize: '16px', color: '#f44336' }} />
                            </IconButton>
                        </Box>
                    ) : existingDocs.aadhaar ? (
                        <Box sx={{
                            padding: '16px',
                            background: '#F3E8FF',
                            borderRadius: '16px',
                            border: '2px solid #724F96',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative',
                            cursor: 'pointer',
                        }}
                        onClick={() => document.getElementById('aadhaar-upload').click()}
                        >
                            <ImageIcon sx={{ fontSize: '32px', color: '#724F96', marginBottom: '8px' }} />
                            <div style={{ color: '#724F96', fontSize: '12px', fontFamily: 'Inter', fontWeight: '600' }}>
                                Previously uploaded
                            </div>
                            <img
                                src={existingDocs.aadhaar}
                                alt="Aadhaar (existing)"
                                style={{
                                    width: '100%',
                                    maxWidth: '200px',
                                    height: 'auto',
                                    borderRadius: '8px',
                                    marginTop: '8px'
                                }}
                            />
                            <div style={{ color: '#666', fontSize: '11px', fontFamily: 'Inter', marginTop: 4 }}>
                                Tap to replace
                            </div>
                        </Box>
                    ) : (
                        <div
                            className={classes.uploadContainer}
                            onClick={() => document.getElementById('aadhaar-upload').click()}
                            style={{
                                border: !formData.aadhaarFile ? '2px dashed #0D0D0D' : '2px dashed #4CAF50',
                                backgroundColor: !formData.aadhaarFile ? 'rgba(0, 0, 0, 0.02)' : 'rgba(76, 175, 80, 0.05)'
                            }}
                        >
                            <div className={classes.uploadIcon}>📄</div>
                            <div className={classes.uploadText}>Click to upload</div>
                            <div className={classes.uploadText}>Aadhaar Card Upload</div>
                            <div className={classes.uploadSubtext}>JPG, JPEG, PNG less than 1MB</div>
                        </div>
                    )}
                    <input
                        id="aadhaar-upload"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload('aadhaarFile', e)}
                    />
                </div>

                {/* Driving License Number */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <label className={classes.label}>Driving License Number</label>
                        <span style={{ color: '#FF4444', fontSize: '16px', fontWeight: '600' }}>*</span>
                    </div>
                    <input
                        className={classes.fullWidthInput}
                        type="text"
                        value={formData.drivingLicenseNumber}
                        onChange={(e) => handleInputChange('drivingLicenseNumber', e.target.value)}
                        placeholder="Enter license number (e.g., TS00120251234567)"
                        style={{
                            borderColor: formData.drivingLicenseNumber && !isValidLicense(formData.drivingLicenseNumber) ? '#FF4444' : '#0D0D0D'
                        }}
                    />
                </div>

                {/* Driving License Upload */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <label className={classes.label}>Driving License Upload</label>
                        <span style={{ color: '#FF4444', fontSize: '16px', fontWeight: '600' }}>*</span>
                        <span style={{ 
                            color: '#FF4444', 
                            fontSize: '12px', 
                            fontWeight: '500', 
                            backgroundColor: 'rgba(255, 68, 68, 0.1)', 
                            padding: '2px 6px', 
                            borderRadius: '4px' 
                        }}>Required</span>
                    </div>
                </div>
                <div className={classes.uploadSection}>
                    {formData.drivingLicenseFile ? (
                        <Box sx={{
                            padding: '16px',
                            background: '#E8F5E8',
                            borderRadius: '16px',
                            border: '2px solid #4CAF50',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative'
                        }}>
                            <ImageIcon sx={{ fontSize: '32px', color: '#4CAF50', marginBottom: '8px' }} />
                            <div style={{ color: '#2E7D32', fontSize: '12px', fontFamily: 'Inter', fontWeight: '600' }}>
                                Driving License Uploaded
                            </div>
                            <div style={{ color: '#666', fontSize: '11px', fontFamily: 'Inter' }}>
                                {formData.drivingLicenseFile.name}
                            </div>
                            <img
                                src={filePreviews.drivingLicenseFile}
                                alt="Driving License preview"
                                style={{
                                    width: '100%',
                                    maxWidth: '200px',
                                    height: 'auto',
                                    borderRadius: '8px',
                                    marginTop: '8px'
                                }}
                            />
                            <IconButton
                                onClick={() => removeFile('drivingLicenseFile')}
                                sx={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    '&:hover': { background: 'rgba(255, 255, 255, 1)' }
                                }}
                            >
                                <Close sx={{ fontSize: '16px', color: '#f44336' }} />
                            </IconButton>
                        </Box>
                    ) : existingDocs.license ? (
                        // Previously-uploaded license (S3 presigned URL). Click
                        // to replace; existing image already satisfies the
                        // "file required" gate so the resubmitter can submit
                        // without re-uploading.
                        <Box sx={{
                            padding: '16px',
                            background: '#F3E8FF',
                            borderRadius: '16px',
                            border: '2px solid #724F96',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative',
                            cursor: 'pointer',
                        }}
                        onClick={() => document.getElementById('license-upload').click()}
                        >
                            <ImageIcon sx={{ fontSize: '32px', color: '#724F96', marginBottom: '8px' }} />
                            <div style={{ color: '#724F96', fontSize: '12px', fontFamily: 'Inter', fontWeight: '600' }}>
                                Previously uploaded
                            </div>
                            <img
                                src={existingDocs.license}
                                alt="Driving License (existing)"
                                style={{
                                    width: '100%',
                                    maxWidth: '200px',
                                    height: 'auto',
                                    borderRadius: '8px',
                                    marginTop: '8px'
                                }}
                            />
                            <div style={{ color: '#666', fontSize: '11px', fontFamily: 'Inter', marginTop: 4 }}>
                                Tap to replace
                            </div>
                        </Box>
                    ) : (
                        <div
                            className={classes.uploadContainer}
                            onClick={() => document.getElementById('license-upload').click()}
                            style={{
                                border: !formData.drivingLicenseFile ? '2px dashed #FF4444' : '2px dashed #4CAF50',
                                backgroundColor: !formData.drivingLicenseFile ? 'rgba(255, 68, 68, 0.05)' : 'rgba(76, 175, 80, 0.05)'
                            }}
                        >
                            <div className={classes.uploadIcon}>📄</div>
                            <div className={classes.uploadText}>Click to upload</div>
                            <div className={classes.uploadText}>Driving License Upload</div>
                            <div className={classes.uploadSubtext}>JPG, JPEG, PNG less than 1MB</div>
                            {!formData.drivingLicenseFile && (
                                <div style={{ color: '#FF4444', fontSize: '10px', marginTop: '4px', fontWeight: '600' }}>
                                    Required
                                </div>
                            )}
                        </div>
                    )}
                    <input
                        id="license-upload"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload('drivingLicenseFile', e)}
                    />
                </div>

                {/* Expiry Date */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <label className={classes.label}>Expiry Date</label>
                        <span style={{ color: '#FF4444', fontSize: '16px', fontWeight: '600' }}>*</span>
                        <span style={{ 
                            color: '#FF4444', 
                            fontSize: '12px', 
                            fontWeight: '500', 
                            backgroundColor: 'rgba(255, 68, 68, 0.1)', 
                            padding: '2px 6px', 
                            borderRadius: '4px' 
                        }}>Required</span>
                    </div>
                    <input
                        className={classes.fullWidthInput}
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        max="2100-12-31"
                        value={formData.expiryDate}
                        onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                        style={{
                            borderColor: formData.expiryDate && !isValidDate(formData.expiryDate) ? '#FF4444' : '#0D0D0D'
                        }}
                    />
                    {formData.expiryDate && !isValidDate(formData.expiryDate) && (
                        <div style={{ color: '#FF4444', fontSize: '12px', marginTop: '4px', fontFamily: 'Nunito' }}>
                            Please enter a valid future date (license must not be expired)
                        </div>
                    )}
                </div>

                {error && (
                    <div className={classes.errorText}>
                        {error}
                    </div>
                )}

                {/* Form Status Message */}
                {!isFormValid() && (
                    <div style={{
                        color: '#f44336',
                        fontSize: '14px',
                        textAlign: 'center',
                        marginTop: '16px',
                        marginBottom: '16px',
                        fontFamily: 'Nunito',
                        fontWeight: '500',
                        padding: '12px',
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(244, 67, 54, 0.3)'
                    }}>
                        <div style={{ fontWeight: '600', marginBottom: '8px' }}>Please fix the following issues:</div>
                        <div style={{ textAlign: 'left', fontSize: '12px' }}>
                            {!formData.phone.trim() && <div>• Phone Number is required</div>}
                            {formData.phone && !isValidPhone(formData.phone) && <div>• Phone Number must be 10 digits</div>}
                            {formData.aadhaarNumber && formData.aadhaarNumber.trim() && !isValidAadhaar(formData.aadhaarNumber) && <div>• Aadhaar Number must be 12 digits</div>}
                            {!formData.drivingLicenseNumber.trim() && <div>• Driving License Number is required</div>}
                            {formData.drivingLicenseNumber.trim() && !isValidLicense(formData.drivingLicenseNumber) && <div>• Driving License Number format is invalid</div>}
                            {!formData.expiryDate.trim() && <div>• Expiry Date is required</div>}
                            {formData.expiryDate && !isValidDate(formData.expiryDate) && <div>• Expiry Date must be a valid future date</div>}
                            {!formData.drivingLicenseFile && !existingDocs.license && <div>• Driving License image upload is required</div>}
                        </div>
                    </div>
                )}

                {/* Success Message when form is valid */}
                {isFormValid() && (
                    <div style={{
                        color: '#4CAF50',
                        fontSize: '14px',
                        textAlign: 'center',
                        marginTop: '16px',
                        marginBottom: '16px',
                        fontFamily: 'Nunito',
                        fontWeight: '500',
                        padding: '12px',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(76, 175, 80, 0.3)'
                    }}>
                        ✅ All required fields are filled and valid. You can now submit the form.
                    </div>
                )}
            </form>

            <button
                type="submit"
                className={classes.submitButton}
                onClick={handleSubmit}
                disabled={isLoading || !isFormValid()}
                style={{
                    background: isFormValid()
                        ? 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)'
                        : 'linear-gradient(90deg, rgba(192, 132, 252, 0.50) 0%, rgba(114.29, 78.57, 150, 0.50) 100%)',
                    boxShadow: isFormValid()
                        ? '0px 9px 14px rgba(192, 132, 252, 0.30)'
                        : '0px 9px 14px rgba(192, 132, 252, 0.20)',
                    opacity: isFormValid() ? 1 : 0.5,
                    cursor: isFormValid() ? 'pointer' : 'not-allowed',
                    transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                    console.log('Button hover - isFormValid():', isFormValid());
                    if (isFormValid()) {
                        e.target.style.background = 'linear-gradient(90deg, rgba(192, 132, 252, 0.80) 0%, rgba(114.29, 78.57, 150, 0.80) 100%)';
                        e.target.style.transform = 'translateX(-50%) translateY(-2px)';
                        e.target.style.boxShadow = '0px 12px 20px rgba(192, 132, 252, 0.40)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (isFormValid()) {
                        e.target.style.background = 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)';
                        e.target.style.transform = 'translateX(-50%) translateY(0px)';
                        e.target.style.boxShadow = '0px 9px 14px rgba(192, 132, 252, 0.30)';
                    }
                }}
            >
                {isLoading ? 'Submitting...' : (isFormValid() ? 'Submit Details' : 'Fill Required Fields')}
            </button>
        </div>
    );
};

export default PersonalDetails;
